import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Box,
  Typography,
  styled,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { submitVideoRating } from '../apiCalls/feedback/feedback';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#FFE8BC',
    borderRadius: 16,
    padding: theme.spacing(2),
    minWidth: 300,
    minHeight: 200,
    border: 'black 2px solid',
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      minWidth: '95%',
      margin: '10px',
      padding: theme.spacing(1),
    }
  }
}));

const HeaderButton = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFA800',
  color: 'black',
  padding: '8px 16px',
  borderRadius: 24,
  fontWeight: 600,
  border: 'black 2px solid',
  position: 'absolute',
  top: '-20px',
  left: '50%',
  transform: 'translateX(-50%)',
  maxWidth: 230,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '0.9rem',
    padding: '6px 12px',
    whiteSpace: 'nowrap',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FAAE1C',
  color: 'black',
  borderRadius: 16,
  padding: '10px 20px',
  '&:hover': {
    backgroundColor: '#e69e1a',
  },
  '&.Mui-disabled': {
    backgroundColor: '#FAAE1C',
    color: 'black',
    opacity: 0.6
  },
  fontWeight: 600,
  border: '1px solid black',
  textTransform: 'none'
}));

const StarButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  '&:hover': {
    backgroundColor: 'transparent'
  }
}));

const RatingDialog = ({ open, onClose, contentId, contentType, onOpen }) => {
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleStarClick = (starRating) => {
    setRating(starRating);
  };

  useEffect(() => {
    if (open && onOpen) {
      onOpen();
    }
  }, [open, onOpen]);

  const handleSubmit = async () => {
    if (!rating || !contentId) return;

    try {
      setSubmitting(true);
      setError('');
      
      const response = await submitVideoRating({
        type: contentType,
        content_id: contentId,
        rating: rating.toString()
      });
      
      onClose();
      
    } catch (error) {
      console.error('Error submitting rating:', error);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setError('');
    onClose();
  };

  if (!open) return null;

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box>
        <HeaderButton>
          Rate this content
        </HeaderButton>
        <IconButton
          onClick={handleClose}
          sx={{
            '&:hover': { backgroundColor: '#dbc69f' },
            position: 'absolute',
            top: '-20px',
            right: '-10px',
            border: '2px solid black',
            color: 'black',
            backgroundColor: '#FFE8BC',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ p: 3, mt: 4 }}>
        <Typography
          variant="h6"
          align="center"
          sx={{ 
            mb: 3, 
            color: 'black',
            fontWeight: 600
          }}
        >
          How would you rate this content?
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <StarButton
              key={star}
              onClick={() => handleStarClick(star)}
              sx={{
                color: star <= rating ? '#FFA800' : '#ccc'
              }}
            >
              {star <= rating ? 
                <StarIcon sx={{ fontSize: 40 }} /> : 
                <StarBorderIcon sx={{ fontSize: 40 }} />
              }
            </StarButton>
          ))}
        </Box>


        <Box display="flex" justifyContent="center" gap={2}>
          <StyledButton 
            onClick={handleSubmit} 
            disabled={!rating || submitting}
            variant="contained"
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
          </StyledButton>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
};

export default RatingDialog;